import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
    Settings,
    Eye,
    EyeOff,
    Save,
    Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberSettingsPage = () => {
    const { member, updateUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [privacySettings, setPrivacySettings] = useState({
        show_email: false,
        show_phone: false,
        show_location: true,
        show_profession: true,
        show_education: false,
        show_marital_status: false,
        show_children: false,
        show_siblings: false,
        show_hobbies: true,
        show_employer: false,
        show_birth_city: false
    });

    useEffect(() => {
        if (member?.privacy_settings) {
            setPrivacySettings({
                show_email: member.privacy_settings.show_email ?? false,
                show_phone: member.privacy_settings.show_phone ?? false,
                show_location: member.privacy_settings.show_location ?? true,
                show_profession: member.privacy_settings.show_profession ?? true,
                show_education: member.privacy_settings.show_education ?? false,
                show_marital_status: member.privacy_settings.show_marital_status ?? false,
                show_children: member.privacy_settings.show_children ?? false,
                show_siblings: member.privacy_settings.show_siblings ?? false,
                show_hobbies: member.privacy_settings.show_hobbies ?? true,
                show_employer: member.privacy_settings.show_employer ?? false,
                show_birth_city: member.privacy_settings.show_birth_city ?? false
            });
        }
    }, [member]);

    const handleToggle = (field) => {
        setPrivacySettings(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Privacy fields that can be toggled (excluding visible_in_directory which is separate)
    const privacyFields = [
        'show_email', 'show_phone', 'show_location', 'show_profession',
        'show_employer', 'show_education', 'show_birth_city', 
        'show_marital_status', 'show_children', 'show_siblings', 'show_hobbies'
    ];

    const allSelected = privacyFields.every(field => privacySettings[field]);

    const handleSelectAll = () => {
        const newValue = !allSelected;
        const updates = {};
        privacyFields.forEach(field => {
            updates[field] = newValue;
        });
        setPrivacySettings(prev => ({
            ...prev,
            ...updates
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const response = await axios.put(`${API}/member/profile`, {
                privacy_settings: privacySettings
            });
            updateUser(response.data);
            toast.success('Privacy settings updated!');
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setLoading(false);
        }
    };

    const PrivacyToggle = ({ field, label, description }) => (
        <div className="flex items-center justify-between py-4 border-b border-[#5A6350] last:border-0">
            <div className="flex-1">
                <p className="text-white font-medium">{label}</p>
                {description && <p className="text-[#666] text-sm mt-1">{description}</p>}
            </div>
            <button
                onClick={() => handleToggle(field)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                    privacySettings[field] ? 'bg-[#C9A227]' : 'bg-[#5A6350]'
                }`}
                data-testid={`toggle-${field}`}
            >
                <span
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        privacySettings[field] ? 'left-7' : 'left-1'
                    }`}
                />
            </button>
        </div>
    );

    return (
        <div className="space-y-6 max-w-3xl" data-testid="member-settings">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-white font-['Playfair_Display'] flex items-center gap-3">
                    <Settings className="w-8 h-8 text-[#C9A227]" />
                    Settings
                </h1>
                <p className="text-[#A0A0A0] mt-1">
                    Manage your privacy and visibility preferences
                </p>
            </div>

            {/* Privacy Settings */}
            <Card className="bg-[#4A5240] border-[#5A6350]">
                <CardHeader className="pb-3">
                    <CardTitle className="text-white font-['Playfair_Display'] flex items-center gap-2">
                        <Shield className="w-5 h-5 text-[#C9A227]" />
                        Profile Privacy
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-[#A0A0A0] text-sm">
                            Control what information other members can see on your profile
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleSelectAll}
                            className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10"
                            data-testid="privacy-select-all-btn"
                        >
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                    </div>
                    
                    <div className="divide-y divide-[#5A6350]">
                        <PrivacyToggle 
                            field="show_email" 
                            label="Show Email Address"
                            description="Allow members to see your email"
                        />
                        <PrivacyToggle 
                            field="show_phone" 
                            label="Show Phone Number"
                            description="Allow members to see your phone number"
                        />
                        <PrivacyToggle 
                            field="show_location" 
                            label="Show Location"
                            description="Display your current city/area"
                        />
                        <PrivacyToggle 
                            field="show_profession" 
                            label="Show Profession"
                            description="Display your profession/occupation"
                        />
                        <PrivacyToggle 
                            field="show_employer" 
                            label="Show Employer"
                            description="Display where you work"
                        />
                        <PrivacyToggle 
                            field="show_education" 
                            label="Show Education"
                            description="Display your education level"
                        />
                        <PrivacyToggle 
                            field="show_birth_city" 
                            label="Show Birth City"
                            description="Display where you were born"
                        />
                        <PrivacyToggle 
                            field="show_marital_status" 
                            label="Show Marital Status"
                            description="Display your relationship status"
                        />
                        <PrivacyToggle 
                            field="show_children" 
                            label="Show Children Info"
                            description="Display if you have children"
                        />
                        <PrivacyToggle 
                            field="show_siblings" 
                            label="Show Siblings Info"
                            description="Display if you have siblings"
                        />
                        <PrivacyToggle 
                            field="show_hobbies" 
                            label="Show Hobbies"
                            description="Display your hobbies and interests"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Save Button */}
            <div className="flex justify-end">
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold px-8"
                    data-testid="save-settings-btn"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {loading ? 'Saving...' : 'Save Settings'}
                </Button>
            </div>
        </div>
    );
};

export default MemberSettingsPage;
